import { Component, Input, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ImportDataService } from 'app/core/services/import-data.service';
import { concatMap, delay, from, of, tap } from 'rxjs';

@Component({
  selector: 'modal-import-default-schedule',
  templateUrl: './modal-import-default-schedule.component.html',
  styleUrls: ['./modal-import-default-schedule.component.scss']
})
export class ModalImportDefaultScheduleComponent implements OnInit, AfterViewInit {

  isLoading    = false;
  formatError  = false;
  isInserting  = false;
  isImported   = false;
  displayTable = true;
  insertionMessage: string = null;

  @Input() importedData: any[] = [];
  selectedRows: any[] = [];
  dataSource = new MatTableDataSource<any>([]);

  displayedColumns: string[] = [
    'select', 'rowType',
    'description', 'employee', 'payroll', 'department', 'validFrom', 'validTo',
    'weekday', 'isRestDay', 'time1', 'time2'
  ];

  expectedHeaders: string[] = [
    'description', 'employee', 'payroll', 'department', 'validFrom', 'validTo',
    'weekday', 'isRestDay', 'time1', 'time2', 'time3', 'time4', 'time5', 'time6',
    'addMissingTime', 'computeRestDay', 'computeHoliday', 'adjustOT', 'computeNightDiff'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(public activeModal: NgbActiveModal, private importDataService: ImportDataService) {}

  ngOnInit(): void {
    this.importedData.forEach(row => {
      const hasLine = row.weekday?.toString().trim();
      if (row.description?.toString().trim()) {
        row._rowType = hasLine ? 'header+line' : 'header';
      } else {
        row._rowType = 'line';
      }
    });

    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedRows = [...this.importedData];

    if (!this.isDataFormatCorrect()) this.formatError = true;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  isDataFormatCorrect(): boolean {
    if (!this.importedData.length) return true;
    const keys = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(h => keys.includes(h));
  }

  onCheckboxChange(row: any, checked: boolean): void {
    if (checked) { this.selectedRows.push(row); }
    else {
      const i = this.selectedRows.indexOf(row);
      if (i !== -1) this.selectedRows.splice(i, 1);
    }
  }

  isSelected(row: any): boolean { return this.selectedRows.includes(row); }

  toggleSelectAll(checked: boolean): void {
    this.selectedRows = checked ? [...this.importedData] : [];
  }

  toFlag(v: any): boolean {
    return v === true || v === 'Y' || v === 'y' || v === 'true';
  }

  toWeekday(v: any): number {
    const map: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    };
    if (typeof v === 'number') return v;
    return map[v?.toString().toLowerCase().trim()] ?? 0;
  }

  saveSelectedScales(): void {
    // Group rows: header starts a group, line rows attach to the last header
    const groups: Array<{ header: any; lines: any[] }> = [];
    let current: { header: any; lines: any[] } | null = null;

    this.selectedRows.forEach(row => {
      if (row._rowType === 'header' || row._rowType === 'header+line') {
        current = { header: row, lines: [] };
        groups.push(current);
        if (row._rowType === 'header+line') current.lines.push(row);
      } else if (current) {
        current.lines.push(row);
      }
    });

    const total = groups.length;
    let done = 0;

    this.isInserting = true;
    this.displayTable = false;
    this.isLoading = true;
    this.insertionMessage = 'Please wait...';

    from(groups).pipe(
      concatMap(group =>
        of(group).pipe(
          delay(800),
          concatMap(g => {
            const h = g.header;
            const payload = {
              description: h.description,
              employee:    h.employee    || '',
              payroll:     h.payroll     || '',
              department:  h.department  || '',
              validFrom:   h.validFrom   || null,
              validTo:     h.validTo     || null,
              lines: g.lines.map(l => ({
                weekday:               this.toWeekday(l.weekday),
                isRestDay:             this.toFlag(l.isRestDay),
                rampTime1:             l.time1    || null,
                rampTime2:             l.time2    || null,
                rampTime3:             l.time3    || null,
                rampTime4:             l.time4    || null,
                rampTime5:             l.time5    || null,
                rampTime6:             l.time6    || null,
                RAMP_isAddMissingTime: this.toFlag(l.addMissingTime),
                RAMP_isComputeRestDay: this.toFlag(l.computeRestDay),
                RAMP_isComputeHoliday: this.toFlag(l.computeHoliday),
                RAMP_isAdjustOT:       this.toFlag(l.adjustOT),
                RAMP_isComputeNightDiff: this.toFlag(l.computeNightDiff),
              })),
            };

            done++;
            this.insertionMessage = `Inserting schedule ${done} of ${total} with ${g.lines.length} line(s)...`;
            console.log('Default schedule payload:', JSON.stringify(payload, null, 2));

            return this.importDataService.insertDefaultScheduleFromCSV(payload).pipe(
              tap({ error: err => console.error('Insert error', err) })
            );
          })
        )
      )
    ).subscribe({
      next: () => {},
      error: err => {
        console.error(err);
        this.isLoading = false;
        this.isInserting = false;
        this.insertionMessage = 'An error occurred. Please try again.';
      },
      complete: () => {
        this.insertionMessage = `Imported ${total} schedule(s) successfully.`;
        this.isImported  = true;
        this.isInserting = false;
        this.isLoading   = false;
      }
    });
  }

  onClose(): void {
    this.activeModal.dismiss('cancel');
    window.location.reload();
  }
}
